import * as Sentry from "@sentry/browser";
import * as ReactSentry from "@sentry/react";
import type { RouterHistory } from "@sentry/react/build/types/reactrouter";
import { Route } from "react-router-dom";
import { isDefined } from "../utils/helpers";

function getSettings() {
  return window.APP_SETTINGS ?? {};
}

// Production app any endpoints matching the explicit fetch will be considered for tracing
// This is to ensure we catch the API Proxy requests which will make fetch requests with a full URL without the inclusion of presign in the URL
const tracePropagationTargets = [
  // Only propagate tracing headers for same-origin paths, excluding the resolve/presign endpoints
  /^\/(?!tasks\/\d+\/(?:resolve|presign)|projects\/\d+\/(?:resolve|presign))/,
];

export const initSentry = (history: RouterHistory) => {
  const settings = getSettings();
  const dsn = settings.sentry_dsn;
  const env = settings.sentry_environment ?? process.env.NODE_ENV;
  const rate = settings.sentry_rate ? Number.parseFloat(settings.sentry_rate) : 0.25;
  const enabled = settings.debug === false && isDefined(dsn);

  if (enabled) {
    setTags();

    Sentry.init({
      dsn,
      tracePropagationTargets,
      integrations: [
        Sentry.browserTracingIntegration(),
        ReactSentry.reactRouterV5BrowserTracingIntegration({ history }),
      ],
      environment: env,
      tracesSampleRate: rate,
      release: getVersion(),
    });
  }
};

function isSentryEnabled() {
  const settings = getSettings();
  return settings.debug === false && isDefined(settings.sentry_dsn);
}

export const captureMessage: typeof Sentry.captureMessage = (message, type) => {
  if (!isSentryEnabled()) {
    if (typeof type === "string" && type in console) {
      (console as any)[type](message);
    } else {
      console.log(message);
    }
    return "";
  }
  return Sentry.captureMessage(message, type);
};

export const captureException: typeof Sentry.captureException = (exception, captureContext) => {
  if (!isSentryEnabled()) {
    console.error(exception, captureContext);
    return "";
  }

  if (captureContext && "extra" in captureContext && captureContext.extra?.sentry_skip) {
    return "";
  }

  return Sentry.captureException(exception, captureContext);
};

const setTags = () => {
  const settings = getSettings();
  const tags: Record<string, any> = {};

  if (settings.user?.email) {
    Sentry.setUser({
      email: settings.user.email,
      username: settings.user.username,
    });
  }

  if (settings.version) {
    Object.entries(settings.version).forEach(([packageName, data]: [string, any]) => {
      const { version, commit } = data ?? {};

      if (version) {
        tags[`version-${packageName}`] = version;
      }
      if (commit) {
        tags[`commit-${packageName}`] = commit;
      }
    });
  }

  Sentry.setTags(tags);
};

const getVersion = () => {
  const settings = getSettings();
  const version = settings.version?.["label-studio-os-package"]?.version;

  return version ? version : process.env.RELEASE_NAME;
};

export const SentryRoute = ReactSentry.withSentryRouting(Route);
